import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get_logs_data } from '../../reduxData/user/userAction';
import CustomPagination from '../../components/CustomPagination';
import EmptyData from '../../components/EmptyData';
import { Tooltip } from "@mui/joy";
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import { formatDateTime } from '../../hooks/formatDateTime';
import { Search, SlidersHorizontal, X } from 'lucide-react';

const AuditLogs = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useSelector((state) => state.auth);
    const { logsData = [], totalLogs = 0 } = useSelector((state) => state.user);
    const dispatch = useDispatch();

    const [searchTerm, setSearchTerm] = useState("");
    const [actionFilter, setActionFilter] = useState("All");
    const [roleFilter, setRoleFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [selectedDate, setSelectedDate] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);


    // fetch logs tracking data function 
    const fetchLogs = async (page = 1, rows = 10) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', rows);
        if (searchTerm.trim()) params.append('search', searchTerm.trim());
        if (actionFilter !== "All") params.append('event', actionFilter);
        if (roleFilter !== "All") params.append('role', roleFilter);
        if (statusFilter !== "All") params.append('status', statusFilter === "true");
        if (selectedDate) {
            params.append("date", dayjs(selectedDate).format("YYYY-MM-DD"));
        }
        await get_logs_data(dispatch, page, rows, params.toString());
    };

    // call fetch data function 
    useEffect(() => {
        fetchLogs(currentPage, perPage);
    }, [searchTerm, actionFilter, roleFilter, statusFilter, selectedDate, currentPage, perPage]);

    // get device info 
    const getDeviceInfo = (userAgent) => {
        if (!userAgent) return t("auditLogs.unknownDevice");
        let device = userAgent.includes("Mobile") ? t("auditLogs.mobile") : t("auditLogs.desktop");
        let browser = t("auditLogs.unknownBrowser");
        if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) browser = t("auditLogs.chrome");
        else if (userAgent.includes("Firefox")) browser = t("auditLogs.firefox");
        else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) browser = t("auditLogs.safari");
        else if (userAgent.includes("Edg")) browser = t("auditLogs.edge");
        return `${device} • ${browser}`;
    };

    // handle status badge classes 
    const getStatusBadge = (status) => {
        return status ? (
            <span className="status-badge success">{t("auditLogs.success")}</span>
        ) : (
            <span className="status-badge failed">{t("auditLogs.failed")}</span>
        );
    };

    // clear all filters function 
    const clearFilters = () => {
        setSearchTerm("");
        setActionFilter("All");
        setRoleFilter("All");
        setStatusFilter("All");
        setSelectedDate(null);
        setCurrentPage(1);
    };

    const datePickerTextFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            backgroundColor: '#fff',
            height: '42px',
            fontSize: '14px',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e8671e',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#e8671e',
        },
        '& .MuiSvgIcon-root': {
            color: '#e8671e',
        },
    };

    const handleDateChange = (newValue) => {
        setSelectedDate(newValue);
        setCurrentPage(1);
    };

    // handle page change function 
    const handlePageChange = (page, rows) => {
        setCurrentPage(page);
        setPerPage(rows);
    };

    return (
        <div className="container audit-logs-page">
            <div className="audit-page-header">
                <div>
                    <h2>{t("auditLogs.title")}</h2>
                    <p>{t("auditLogs.subtitle")}</p>
                </div>
            </div>

            <section className="category-filter-card">
                <div className="category-filter-heading">
                    <span className="category-filter-icon" aria-hidden="true">
                        <SlidersHorizontal size={17} />
                    </span>
                    <div>
                        <strong>{t("auditLogs.findLogs")}</strong>
                        <small>{t("auditLogs.filterHint")}</small>
                    </div>
                </div>

                <div className="category-filter-controls audit-log-filter-controls">
                    <div className="category-filter-search">
                        <Search size={17} aria-hidden="true" />
                        <input
                            type="search"
                            aria-label={t("auditLogs.searchUser")}
                            placeholder={t("auditLogs.searchPlaceholder")}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>

                    <label className="category-filter-field">
                        <span>{t("auditLogs.event")}</span>
                        <select
                            value={actionFilter}
                            onChange={(e) => {
                                setActionFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="All">{t("auditLogs.allEvents")}</option>
                            <option value="login">{t("auditLogs.login")}</option>
                            <option value="logout">{t("auditLogs.logout")}</option>
                        </select>
                    </label>

                    <label className="category-filter-field">
                        <span>{t("auditLogs.role")}</span>
                        <select
                            value={roleFilter}
                            onChange={(e) => {
                                setRoleFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="All">{t("auditLogs.allRoles")}</option>
                            <option value="seller">{t("auditLogs.seller")}</option>
                            <option value="buyer">{t("auditLogs.buyer")}</option>
                            <option value="admin">{t("auditLogs.admin")}</option>
                        </select>
                    </label>

                    <label className="category-filter-field">
                        <span>{t("auditLogs.status")}</span>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="All">{t("auditLogs.allStatus")}</option>
                            <option value="true">{t("auditLogs.success")}</option>
                            <option value="false">{t("auditLogs.failed")}</option>
                        </select>
                    </label>

                    <label className="category-filter-field audit-date-filter">
                        <span>{t("auditLogs.date")}</span>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={selectedDate}
                                maxDate={dayjs()}
                                onChange={handleDateChange}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        placeholder: t("auditLogs.selectDate", { defaultValue: "Select date" }),
                                        sx: datePickerTextFieldSx,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </label>

                    <button
                        className="category-clear-filters"
                        type="button"
                        onClick={clearFilters}
                    >
                        <X size={16} aria-hidden="true" />
                        {t("auditLogs.clearFilters")}
                    </button>
                </div>
            </section>

            <div className="category-card audit-log-table-card">
              <div className="table-responsive">
                <table className="modern-table audit-management-table">
                    <thead>
                        <tr>
                            <th>{t("auditLogs.date")}</th>
                            <th>{t("auditLogs.user")}</th>
                            <th>{t("auditLogs.role")}</th>
                            <th>{t("auditLogs.action")}</th>
                            <th>{t("auditLogs.ipAddress")}</th>
                            <th>{t("auditLogs.deviceBrowser")}</th>
                            <th>{t("auditLogs.message")}</th>
                            <th>{t("auditLogs.status")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(logsData) && logsData.length > 0 ? (
                            logsData.map((item, index) => (
                                <tr key={item.id || index}>
                                    <td>
                                        <div className="audit-date">
                                            <strong>
                                                {formatDateTime(item.created_at, i18n)}
                                            </strong>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar">
                                                {item.user_name?.charAt(0)}
                                            </div>

                                            <div>
                                                <div className="user-name">
                                                    {item.user_name}
                                                </div>

                                                <div className="user-email">
                                                    {item.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`role-badge role-${item.role} text-capitalize`}>
                                            {item.role} 
                                        </span>
                                    </td>
                                    <td><span
                                        className={`action-pill text-capitalize ${item.status ? "success" : "failed"
                                            }`}
                                    >
                                        {item.action_type}
                                    </span></td>
                                    <td>
                                        <Tooltip title={item.ip_address} arrow>
                                            <span className="ip-address">{item.ip_address}</span>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <div className="device-card">
                                            💻 {getDeviceInfo(item.user_agent)}
                                        </div>
                                    </td>
                                    <td className="message-col">{item.message || t("common.naFallback")}</td>
                                    <td>
                                        <span className={`action-pill ${item.status ? "success" : "failed"}`}>
                                            {item.status ? t("auditLogs.success") : t("auditLogs.failed")}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <EmptyData />
                        )}
                    </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4">
                {totalLogs > 0 && (
                    <CustomPagination
                        total={totalLogs}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
};

export default AuditLogs;
